import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Divider,
} from '@mui/material';
import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';
import { REMEDIATION_VERIFICATION_STATUS, REMEDIATION_VERIFICATION_STATUS_LABELS, VerificationStatus } from '../../../services/surveyHelpers';
import moment from 'moment';
import { Badge } from '../../../interfaces/users.interface';
import { BADGE_LABEL_IMAGEE_MAP, GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL } from '../../../services/badge';

interface VerificationFlowDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  remediationQuestionAnswers: any[];
  companyName: string;
  assessmentDate: string;
  navigationLink: string;
  badges: Badge[];
  assignedTPNAdminReviewer: string;
  handleSaveVerificationStatus: (id: string, status: VerificationStatus | null, adminComment: string) => void;
}

const VerificationFlowDialog: React.FC<VerificationFlowDialogProps> = ({
  isOpen,
  handleClose,
  navigationLink,
  badges,
  remediationQuestionAnswers,
  companyName,
  assessmentDate,
  assignedTPNAdminReviewer,
  handleSaveVerificationStatus,
}) => {

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [adminCommentError, setAdminCommentError] = useState('');
  const [pendingStatus, setPendingStatus] = useState<VerificationStatus | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentItem = remediationQuestionAnswers[currentIndex];
  const [adminComment, setAdminComment] = useState(currentItem?.verificationComment || '');
  const [currentStatus, setCurrentStatus] = useState<VerificationStatus>(
    currentItem?.verificationStatus || REMEDIATION_VERIFICATION_STATUS.notStarted,
  );

  const highestAssessmentBadgeLabel = useMemo(() => {
    if (badges.some((badge) => badge.title === GOLD_STAR_BADGE_LABEL)) return GOLD_STAR_BADGE_LABEL;
    if (badges.some((badge) => badge.title === GOLD_NEXT_GEN_BADGE_LABEL)) return GOLD_NEXT_GEN_BADGE_LABEL;
    if (badges.some((badge) => badge.title === SILVER_BADGE_LABEL)) return SILVER_BADGE_LABEL;
    if (badges.some((badge) => badge.title === GOLD_BADGE_LABEL)) return GOLD_BADGE_LABEL;
    return null;
  }, [badges]);

  useEffect(() => {
    setAdminComment(currentItem?.verificationComment || '');
    setCurrentStatus(currentItem?.verificationStatus || REMEDIATION_VERIFICATION_STATUS.notStarted);
  }, [currentItem]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentIndex(0);
      setAdminCommentError('');
    }
  }, [isOpen]);

  return (
    <>
      <StandardDialog
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedIcon fontSize="medium" />
            <Typography variant="h6" component="span">
              Remediation Validation
            </Typography>
          </Box>
        }
        subtitle="Review and validate Remediated sub-remediations for the selected assessment."
        handleClose={handleClose}
        isOpen={isOpen}
        maxWidth="lg"
      >
        <Stack direction="row" justifyContent="space-between" spacing={3}>
          {/* LEFT Section */}
          <Stack spacing={2} flex={3}>
            <Typography variant="h6">{companyName}</Typography>

            <Typography fontWeight={500}>
              Question: <br />
              <Typography variant="body2">{currentItem?.assessorAnswer?.question?.title || 'N/A'}</Typography>
            </Typography>

            <Typography fontWeight={500}>
              Sub-Control: <br />
              <Typography variant="body2">{currentItem?.answer.text || 'N/A'}</Typography>
            </Typography>

            <Stack direction="column" spacing={2}>
              <Stack direction="column" spacing={1}>
                <Typography variant="caption" fontWeight="bold">
                  Assessor Findings
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography sx={{ maxWidth: 300, fontSize: 14, whiteSpace: 'normal', lineHeight: 1.5 }}>
                        {currentItem?.assessorAnswer?.assessorFinding || ''}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top-start"
                >
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ddd',
                      padding: 1,
                      borderRadius: 1,
                      cursor: 'help',
                    }}
                  >
                    {currentItem?.assessorAnswer?.assessorFinding?.slice(0, 80) || ''}
                  </Typography>
                </Tooltip>

                <Typography variant="caption" fontWeight="bold" mt={2}>
                  SP Remediation Comment
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  value={currentItem?.remediationComment || ''}
                  disabled
                  InputProps={{
                    sx: {
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                    },
                  }}
                />
              </Stack>
              <Link
                to={navigationLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', width: 'fit-content' }}
              >
                <Button
                  variant="text"
                  color="primary"
                  sx={{ alignSelf: 'flex-start', fontWeight: 500, color: '#1976d2', pl: 0 }}>View Assessment
                </Button>
              </Link>
            </Stack>
            <Box>
              <Typography fontWeight={500}>
                Evidence:
              </Typography>
              {
                currentItem?.attachments?.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No evidence provided.
                  </Typography>
                ) : (
                <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                  <Stack spacing={1}>
                    {(currentItem?.attachmentsDetail || []).map((file: any, index: number) => (
                      <Button
                        key={index}
                        variant="text"
                        onClick={() => window.open(file.document, '_blank')}
                        startIcon={<InsertDriveFileIcon />}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {file.document.split('?')[0].split('/').pop()}
                      </Button>
                    ))}
                  </Stack>
                </Box>
                )
              }
            </Box>
          </Stack>
          {/* RIGHT Sidebar */}
          <Stack
            flex={1}
            spacing={2}
            p={2}
            sx={{
              border: '1px solid #e0e0e0',
              backgroundColor: '#f7f7f8',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6">Sub-Remediation Metadata</Typography>
            <Typography variant="subtitle2">Sub-Remediation {currentIndex + 1} of {remediationQuestionAnswers.length}</Typography>
            <Typography variant="subtitle2">Record: {currentItem?.id }</Typography>
            <Typography variant="subtitle2">
              Validation Status:{' '}
              <Box
                component="span"
                sx={{
                  color:
                    currentStatus === REMEDIATION_VERIFICATION_STATUS.reviewed
                      ? 'success.main'
                      : currentStatus === REMEDIATION_VERIFICATION_STATUS.rejected
                        ? 'error.main'
                        : 'text.primary',
                  fontWeight: 600,
                }}
              >
                {REMEDIATION_VERIFICATION_STATUS_LABELS[currentStatus]}
              </Box>
            </Typography>
            <Typography variant="subtitle2">
              Validated:{' '}
              {currentItem?.verificationDate ? moment(currentItem.verificationDate).format('MMM D, YYYY h:mm A') : ''}
            </Typography>
            <Typography variant='subtitle2'>Validated By: {currentItem?.verifiedBy ?
              currentItem.verifiedBy.firstName + ' ' + currentItem.verifiedBy.lastName : ''}</Typography>
            <Typography variant="subtitle2">Assessment Date: {new Date(assessmentDate).toLocaleDateString()}</Typography>
            <Typography variant="subtitle2">Remediation Plan: {currentItem?.remediationPlan || ''}</Typography>
            <Typography variant="subtitle2">Remediation Date: {currentItem?.remediationDate ?
              new Date(currentItem.remediationDate).toLocaleDateString() : ''}</Typography>
            <Typography variant="subtitle2">Has Evidence: {0 < currentItem?.attachments?.length ? 'Yes' : 'No'}</Typography>
            <Typography variant="subtitle2">Status: {currentItem?.status || 'In Progress'}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle2">Current Badge:</Typography>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1px solid #ebebeb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={BADGE_LABEL_IMAGEE_MAP[highestAssessmentBadgeLabel || '']}
                  alt="TPN Logo"
                  style={{
                    width: '80%',
                    height: '80%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
            <Typography variant="subtitle2">TPN Admin Assigned: {assignedTPNAdminReviewer}</Typography>
          </Stack>
        </Stack>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2}>
          <Box sx={{ my: 1 }} flex={1}>
          <TextField
            multiline
            minRows={4}
            label="TPN Admin Comment"
            fullWidth
            value={adminComment}
            onChange={(e) => {
              setAdminComment(e.target.value);
              setAdminCommentError('');
            }}
            error={!!adminCommentError}
            helperText={adminCommentError}
          />
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={2}>
              <Button
                variant={currentStatus === REMEDIATION_VERIFICATION_STATUS.reviewed ? 'contained' : 'outlined'}
                color="success"
                onClick={() => {
                  if (!adminComment) {
                    setAdminCommentError('TPN Admin Comment is required.');
                    return;
                  }
                  setPendingStatus(REMEDIATION_VERIFICATION_STATUS.reviewed);
                  setConfirmDialogOpen(true);
                }}
              >
                REVIEWED
              </Button>

              <Button
                variant={currentStatus === REMEDIATION_VERIFICATION_STATUS.rejected ? 'contained' : 'outlined'}
                color="error"
                onClick={() => {
                  if (!adminComment) {
                    setAdminCommentError('TPN Admin Comment is required.');
                    return;
                  }
                  setPendingStatus(REMEDIATION_VERIFICATION_STATUS.rejected);
                  setConfirmDialogOpen(true);
                }}
              >
                REJECTED
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex(currentIndex - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              disabled={currentIndex === remediationQuestionAnswers.length - 1}
              onClick={() => setCurrentIndex(currentIndex + 1)}
            >
              Next
            </Button>
          </Stack>
        </Stack>

        <StandardDialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {confirmDialogOpen && (
        <StandardDialog
          isOpen={confirmDialogOpen}
          handleClose={() => {
            setConfirmDialogOpen(false);
            setPendingStatus(null);
          }}
          title="Confirm Verification Status Change"
          subtitle={`Are you sure you want to mark this control as ${pendingStatus?.toUpperCase()}?`}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setConfirmDialogOpen(false);
                setPendingStatus(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={pendingStatus === REMEDIATION_VERIFICATION_STATUS.rejected ? 'error' : 'success'}
              onClick={() => {
                if (pendingStatus) {
                  setCurrentStatus(pendingStatus);
                }
                setConfirmDialogOpen(false);
                setPendingStatus(null);
                handleSaveVerificationStatus(currentItem.id, pendingStatus, adminComment);
              }}
            >
              Confirm
            </Button>
          </Box>
        </StandardDialog>
      )}
    </>
  );
};

export default VerificationFlowDialog;
